import React, { useMemo, useState } from "react";
import { useQuery, useMutation } from "@apollo/client";
import Header from "../../../../components/common/header";
import LoadingSpinnerFull from "../../../../components/common/loading-spinner/loading-spinner-full";
import {
  BlockUserMutation,
  BlockUserMutationVariables,
  GetBlacklistedUsersQuery,
  GetBlacklistedUsersQueryVariables,
  GetPackOrdersBySellerQuery,
  GetPackOrdersBySellerQueryVariables,
  GetProductsQuery,
  GetProductsQueryVariables,
  ProductsBySellerDateScope,
  UnblockUserMutation,
  UnblockUserMutationVariables,
} from "../../../../__generated__/graphql";
import {
  BLOCK_USER,
  GET_BLACKLISTED_USERS,
  UNBLOCK_USER,
} from "../../../../api/blacklist";
import { notify, notifyError } from "../../../../utils/toast";
import { formatPhoneNumber } from "../../../../utils/phone";
import { HiXMark } from "react-icons/hi2";
import { GET_PACK_ORDERS } from "../../api/pack-order-queries";
import { GET_PRODUCTS } from "../../api/live-order-queries";

type BlacklistedUser = GetBlacklistedUsersQuery["getBlacklistedUsers"][number];

interface RecentOrderCustomer {
  userId: number;
  name: string;
  phone: string | null;
  latestOrderedAt: string | null;
  orderCount: number;
  isBlocked: boolean;
  sourceLabel: "예약주문" | "실시간";
}

interface ActionModalState {
  mode: "block" | "unblock";
  userId: number;
  userName: string;
  phone: string | null;
}

const LIVE_PRODUCTS_QUERY_VARIABLES: GetProductsQueryVariables = {
  take: 100,
  dateScope: ProductsBySellerDateScope.All,
};

const BlacklistPage: React.FC = () => {
  const {
    data: blacklistData,
    loading: blacklistLoading,
    error: blacklistError,
  } = useQuery<GetBlacklistedUsersQuery, GetBlacklistedUsersQueryVariables>(
    GET_BLACKLISTED_USERS,
    {
      fetchPolicy: "network-only",
    }
  );
  const {
    data: packOrdersData,
    loading: packOrdersLoading,
    error: packOrdersError,
  } = useQuery<GetPackOrdersBySellerQuery, GetPackOrdersBySellerQueryVariables>(
    GET_PACK_ORDERS,
    {
      fetchPolicy: "network-only",
    }
  );
  const {
    data: liveProductsData,
    loading: liveProductsLoading,
    error: liveProductsError,
  } = useQuery<GetProductsQuery, GetProductsQueryVariables>(GET_PRODUCTS, {
    variables: LIVE_PRODUCTS_QUERY_VARIABLES,
    fetchPolicy: "network-only",
  });

  const [blockUser] = useMutation<BlockUserMutation, BlockUserMutationVariables>(
    BLOCK_USER,
    {
      refetchQueries: [
        GET_BLACKLISTED_USERS,
        GET_PACK_ORDERS,
        { query: GET_PRODUCTS, variables: LIVE_PRODUCTS_QUERY_VARIABLES },
      ],
    }
  );
  const [unblockUser] = useMutation<
    UnblockUserMutation,
    UnblockUserMutationVariables
  >(UNBLOCK_USER, {
    refetchQueries: [
      GET_BLACKLISTED_USERS,
      GET_PACK_ORDERS,
      { query: GET_PRODUCTS, variables: LIVE_PRODUCTS_QUERY_VARIABLES },
    ],
  });

  const [actionModal, setActionModal] = useState<ActionModalState | null>(null);

  const blacklistedUsers = useMemo(
    () => blacklistData?.getBlacklistedUsers ?? [],
    [blacklistData?.getBlacklistedUsers]
  );
  const blockedUserIds = useMemo(
    () => new Set(blacklistedUsers.map((blacklist) => blacklist.userId)),
    [blacklistedUsers]
  );

  const recentCustomers = useMemo<RecentOrderCustomer[]>(() => {
    const customerMap = new Map<number, RecentOrderCustomer>();
    const upsertCustomer = (
      userId: number,
      name: string | null | undefined,
      phone: string | null | undefined,
      latestOrderedAt: string | null | undefined,
      sourceLabel: "예약주문" | "실시간"
    ) => {
      const existingCustomer = customerMap.get(userId);
      const currentDate = latestOrderedAt ? new Date(latestOrderedAt) : null;
      const existingDate =
        existingCustomer?.latestOrderedAt
          ? new Date(existingCustomer.latestOrderedAt)
          : null;

      const nextCustomer: RecentOrderCustomer = {
        userId,
        name: name || "익명의 고객",
        phone: phone || null,
        latestOrderedAt: latestOrderedAt || null,
        orderCount: (existingCustomer?.orderCount || 0) + 1,
        isBlocked: blockedUserIds.has(userId),
        sourceLabel,
      };

      if (
        !existingCustomer ||
        !existingDate ||
        (currentDate !== null && currentDate > existingDate)
      ) {
        customerMap.set(userId, nextCustomer);
        return;
      }

      customerMap.set(userId, {
        ...existingCustomer,
        orderCount: existingCustomer.orderCount + 1,
        isBlocked: blockedUserIds.has(userId),
      });
    };

    const packOrders = packOrdersData?.packOrdersBySeller || [];
    packOrders.forEach((order) => {
      if (!order?.user?.id) return;
      upsertCustomer(
        order.user.id,
        order.user.name,
        order.user.phone,
        order.createdAt,
        "예약주문"
      );
    });

    const liveProducts = liveProductsData?.productsBySeller || [];
    liveProducts.forEach((product) => {
      if (product.order?.user?.id) {
        upsertCustomer(
          product.order.user.id,
          product.order.user.name,
          product.order.user.phone,
          product.order.pickUpTime || product.createdAt,
          "실시간"
        );
      }

      product.orderItems?.forEach((orderItem) => {
        if (!orderItem.order.user?.id) return;

        upsertCustomer(
          orderItem.order.user.id,
          orderItem.order.user.name,
          orderItem.order.user.phone,
          orderItem.order.pickUpTime || product.createdAt,
          "실시간"
        );
      });
    });

    return Array.from(customerMap.values()).sort((a, b) => {
      if (!a.latestOrderedAt) return 1;
      if (!b.latestOrderedAt) return -1;
      return (
        new Date(b.latestOrderedAt).getTime() -
        new Date(a.latestOrderedAt).getTime()
      );
    });
  }, [
    blockedUserIds,
    liveProductsData?.productsBySeller,
    packOrdersData?.packOrdersBySeller,
  ]);

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return "없음";
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return "없음";

    return `${date.getFullYear()}.${String(date.getMonth() + 1).padStart(
      2,
      "0"
    )}.${String(date.getDate()).padStart(2, "0")} ${String(
      date.getHours()
    ).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
  };

  const handleBlockClick = (customer: RecentOrderCustomer) => {
    setActionModal({
      mode: "block",
      userId: customer.userId,
      userName: customer.name,
      phone: customer.phone,
    });
  };

  const handleUnblockClick = (blacklist: BlacklistedUser) => {
    setActionModal({
      mode: "unblock",
      userId: blacklist.userId,
      userName: blacklist.user.name || "익명의 고객",
      phone: blacklist.user.phone || null,
    });
  };

  const closeActionModal = () => {
    setActionModal(null);
  };

  const handleActionConfirm = async () => {
    if (!actionModal) return;

    try {
      if (actionModal.mode === "block") {
        const result = await blockUser({
          variables: { userId: actionModal.userId },
        });

        if (result.data?.blockUser?.ok) {
          notify("고객이 차단되었습니다.");
          closeActionModal();
          return;
        }

        notifyError(result.data?.blockUser?.error || "차단에 실패했습니다.");
        return;
      }

      const result = await unblockUser({
        variables: { userId: actionModal.userId },
      });

      if (result.data?.unblockUser?.ok) {
        notify("차단이 해제되었습니다.");
        closeActionModal();
      } else {
        notifyError(
          result.data?.unblockUser?.error || "차단 해제에 실패했습니다."
        );
      }
    } catch (error) {
      notifyError(
        error instanceof Error
          ? error.message
          : actionModal.mode === "block"
          ? "차단에 실패했습니다."
          : "차단 해제에 실패했습니다."
      );
    }
  };

  if (blacklistLoading || packOrdersLoading || liveProductsLoading) {
    return <LoadingSpinnerFull />;
  }

  const error = blacklistError || packOrdersError || liveProductsError;
  if (error) {
    return (
      <div className="w-full min-h-screen flex flex-col">
        <Header title="차단 고객 관리" showBackButton />
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="text-center text-gray-500">
            <div className="text-lg font-medium">에러가 발생했습니다.</div>
            <div className="text-md mt-2">{error.message}</div>
          </div>
        </div>
      </div>
    );
  }

  const selectedDisplayName = actionModal?.userName || "익명의 고객";

  return (
    <div className="w-full min-h-screen flex flex-col bg-[#f4f5f7]">
      <Header title="차단 고객 관리" showBackButton />
      <div className="flex-1 overflow-y-auto p-4">
        <div className="mx-auto w-full max-w-6xl space-y-4">
          <div className="rounded-2xl bg-white p-5 shadow-sm border border-gray-100">
            <h1 className="text-2xl font-bold text-gray-900">차단 고객 관리</h1>
            <p className="mt-2 text-sm text-gray-500">
              최근 주문한 고객을 확인하고 바로 차단하거나, 이미 차단한 고객을
              해제할 수 있어요.
            </p>
            <p className="mt-1 text-xs text-gray-400">
              최근 주문 고객은 예약 주문과 실시간 주문을 함께 보여드려요.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 lg:items-start">
            <section className="rounded-2xl bg-white p-5 shadow-sm border border-gray-100">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">
                    현재 차단 목록
                  </h2>
                  <p className="mt-1 text-sm text-gray-500">
                    현재 차단한 고객 {blacklistedUsers.length}명
                  </p>
                </div>
              </div>

              {blacklistedUsers.length === 0 ? (
                <div className="flex flex-col items-center justify-center rounded-2xl bg-gray-50 py-16 text-center mt-4">
                  <div className="text-5xl mb-3">🚫</div>
                  <div className="text-lg font-semibold text-gray-700">
                    아직 차단한 고객이 없어요
                  </div>
                  <div className="mt-2 text-sm text-gray-500">
                    오른쪽 최근 주문 고객 영역에서 바로 차단할 수 있어요.
                  </div>
                </div>
              ) : (
                <div className="mt-4 space-y-3">
                  {blacklistedUsers.map((blacklist) => (
                    <div
                      key={blacklist.id}
                      className="rounded-2xl border border-gray-200 bg-white p-4"
                    >
                      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                        <div className="flex-1">
                          <div className="text-lg font-semibold text-gray-800">
                            {blacklist.user.name || "익명의 고객"}
                          </div>
                          <div className="mt-2 text-sm text-gray-600">
                            <span className="font-medium">전화번호: </span>
                            {formatPhoneNumber(blacklist.user.phone) || "없음"}
                          </div>
                          {blacklist.user.email && (
                            <div className="mt-1 text-sm text-gray-600">
                              <span className="font-medium">이메일: </span>
                              {blacklist.user.email}
                            </div>
                          )}
                          <div className="mt-2 text-sm text-gray-500">
                            차단일시: {formatDate(blacklist.createdAt)}
                          </div>
                        </div>
                        <button
                          onClick={() => handleUnblockClick(blacklist)}
                          className="rounded-lg bg-red-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-red-600"
                        >
                          차단 해제
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>

            <section className="rounded-2xl bg-white p-5 shadow-sm border border-gray-100">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">
                    최근 주문 고객
                  </h2>
                  <p className="mt-1 text-sm text-gray-500">
                    최근 주문한 고객을 확인하고 바로 차단할 수 있어요.
                  </p>
                </div>
              </div>

              {recentCustomers.length === 0 ? (
                <div className="flex flex-col items-center justify-center rounded-2xl bg-gray-50 py-16 text-center mt-4">
                  <div className="text-5xl mb-3">🧾</div>
                  <div className="text-lg font-semibold text-gray-700">
                    최근 주문 고객이 없어요
                  </div>
                  <div className="mt-2 text-sm text-gray-500">
                    예약 주문과 실시간 주문 고객을 이곳에서 바로 차단할 수
                    있어요.
                  </div>
                </div>
              ) : (
                <div className="mt-4 space-y-3">
                  {recentCustomers.map((customer) => (
                    <div
                      key={customer.userId}
                      className="rounded-2xl border border-gray-200 bg-white p-4"
                    >
                      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                        <div className="flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <div className="text-lg font-semibold text-gray-800">
                              {customer.name}
                            </div>
                            <span className="rounded-full bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-600">
                              {customer.sourceLabel}
                            </span>
                            <span
                              className={`rounded-full px-2.5 py-1 text-xs font-medium ${
                                customer.isBlocked
                                  ? "bg-red-50 text-red-500"
                                  : "bg-emerald-50 text-emerald-600"
                              }`}
                            >
                              {customer.isBlocked ? "이미 차단됨" : "차단 가능"}
                            </span>
                          </div>
                          <div className="mt-2 text-sm text-gray-600">
                            <span className="font-medium">전화번호: </span>
                            {formatPhoneNumber(customer.phone) || "없음"}
                          </div>
                          <div className="mt-1 text-sm text-gray-600">
                            <span className="font-medium">최근 주문: </span>
                            {formatDate(customer.latestOrderedAt)}
                          </div>
                          <div className="mt-1 text-sm text-gray-500">
                            누적 주문 {customer.orderCount}회
                          </div>
                        </div>
                        <button
                          onClick={() => handleBlockClick(customer)}
                          disabled={customer.isBlocked}
                          className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                            customer.isBlocked
                              ? "cursor-not-allowed bg-gray-200 text-gray-500"
                              : "bg-gray-900 text-white hover:bg-gray-800"
                          }`}
                        >
                          {customer.isBlocked ? "차단됨" : "차단"}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </div>
        </div>
      </div>

      {actionModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-2xl font-bold text-gray-800">
                {actionModal.mode === "block"
                  ? "고객을 차단하시겠습니까?"
                  : "차단을 해제하시겠습니까?"}
              </h2>
              <button
                onClick={closeActionModal}
                className="text-gray-500 hover:text-gray-700"
              >
                <HiXMark className="w-6 h-6" />
              </button>
            </div>

            <div className="mb-4 rounded-lg bg-gray-100 p-4">
              <div className="text-base text-gray-600">
                이름:{" "}
                <span className="font-bold text-blue-700">
                  {selectedDisplayName}
                </span>
              </div>
              <div className="mt-2 text-base text-gray-600">
                전화번호:{" "}
                <span className="font-bold text-blue-700">
                  {formatPhoneNumber(actionModal.phone) || "없음"}
                </span>
              </div>
            </div>

            <p className="mb-2 text-lg text-gray-600">
              <span className="font-semibold">{selectedDisplayName}</span>
              {actionModal.mode === "block"
                ? " 고객을 차단하시겠습니까?"
                : " 고객의 차단을 해제하시겠습니까?"}
            </p>
            <p className="mb-6 text-sm text-gray-500">
              {actionModal.mode === "block"
                ? "차단한 고객은 매장을 볼 수 없고 주문도 할 수 없습니다."
                : "차단을 해제하면 다시 주문할 수 있습니다."}
            </p>

            <div className="flex justify-end gap-3">
              <button
                className="rounded-lg bg-gray-500 px-4 py-2 text-white transition-colors hover:bg-gray-600"
                onClick={closeActionModal}
              >
                취소
              </button>
              <button
                className={`rounded-lg px-4 py-2 text-white transition-colors ${
                  actionModal.mode === "block"
                    ? "bg-gray-900 hover:bg-gray-800"
                    : "bg-red-500 hover:bg-red-600"
                }`}
                onClick={handleActionConfirm}
              >
                {actionModal.mode === "block" ? "차단" : "차단 해제"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BlacklistPage;
